/**
 * Gastos previstos de un año (puro): cada uno con su estado y lo que queda por pagar.
 * - Pagado: tiene su pago vinculado (a mano o reconocido solo). Si el importe difiere, «pagado con otro importe».
 * - Pendiente: aún puede pagarse; el pago se reconoce en su mes o el siguiente, así que lo es hasta que acaba el siguiente.
 * - No se pagó: pasó ese plazo sin pago vinculado.
 */
import { round2 } from '@pf/domain';
import type { PlannedRow } from '@pf/workbook';
import type { Computed } from './model.js';
import { appliesTo, plannedIndex } from './planned.js';

export type PlannedStatus = 'paid' | 'paid-diff' | 'pending' | 'missed';
export interface PlannedYearItem { planned: PlannedRow; status: PlannedStatus; paid: number | null; paidDate: string }
export interface PlannedYear {
  year: number; items: PlannedYearItem[];
  /** Previsto en el año, ya pagado (lo real) y lo que falta (de los pendientes). */
  total: number; paid: number; pending: number; missed: number;
}

/** Diferencia a partir de la cual el pago «no es lo previsto»: 1 € o el 2 %. */
const differs = (paid: number, planned: number) => Math.abs(paid - planned) >= Math.max(1, planned * 0.02);
const monthIndex = (year: number, month: number) => year * 12 + month - 1;

export function plannedYear(c: Computed, year: number, today: string): PlannedYear {
  const idx = plannedIndex(c);
  const now = monthIndex(Number(today.slice(0, 4)), Number(today.slice(5, 7)));
  const items = (c.wb.planned ?? []).filter(p => appliesTo(p, year))
    .sort((a, b) => a.month - b.month || a.concept.localeCompare(b.concept, 'es'))
    .map((p): PlannedYearItem => {
      const pay = idx.payments.find(x => x.planned.id === p.id && x.year === year);
      if (pay) {
        const paid = round2(Math.abs(pay.movements.reduce((t, m) => t + m.amount, 0)));
        return { planned: p, status: differs(paid, p.amount) ? 'paid-diff' : 'paid', paid, paidDate: pay.movements[0]?.date ?? '' };
      }
      return { planned: p, status: now > monthIndex(year, p.month) + 1 ? 'missed' : 'pending', paid: null, paidDate: '' };
    });
  const sum = (list: PlannedYearItem[], f: (x: PlannedYearItem) => number) => round2(list.reduce((t, x) => t + f(x), 0));
  return {
    year, items,
    total: sum(items, x => x.planned.amount),
    paid: sum(items.filter(x => x.paid !== null), x => x.paid!),
    pending: sum(items.filter(x => x.status === 'pending'), x => x.planned.amount),
    missed: items.filter(x => x.status === 'missed').length,
  };
}

/** Previstos que aún pueden pasar este mes sin haberse pagado: los de este mes y los del anterior (se reconocen en su mes o el siguiente). Para «Al cierre» de Inicio. */
export function plannedPendingThisMonth(c: Computed, today: string): Array<{ concept: string; amount: number }> {
  const month = Number(today.slice(5, 7));
  return plannedYear(c, Number(today.slice(0, 4)), today).items
    .filter(x => x.status === 'pending' && x.planned.month <= month)
    .map(x => ({ concept: x.planned.concept || x.planned.category, amount: x.planned.amount }));
}

/** Lo que queda de previstos este año (todos los pendientes): para «libre de verdad». */
export const plannedPendingRestOfYear = (c: Computed, today: string): number => plannedYear(c, Number(today.slice(0, 4)), today).pending;
